import { InteractiveBrowserCredential } from '@azure/identity';

import { ConfigApi, createApiRef } from '@backstage/core-plugin-api';

export type ResolvedResourceGroup = {
  /** Full ARM id, e.g. `/subscriptions/<subscriptionId>/resourceGroups/<name>`. */
  id: string;
  name: string;
  subscriptionId: string;
  location: string;
  tags: Record<string, string>;
};

export type TagValueSuggestion = {
  value: string;
  occurrences: number;
};

/**
 * Whether the signed-in user may write tags at a scope. `unknown` means Azure
 * would not tell us — the write is left available and fails loudly if it is
 * really not permitted.
 */
export type TagWriteAccess = 'allowed' | 'denied' | 'unknown';

export interface ArmTagsApi {
  /**
   * Resolves resource group names to their ARM id, subscription, location and
   * current tags. Only resource groups the signed-in user can read come back, so
   * names with no match are either invisible to them or no longer exist.
   *
   * A name is not unique across the tenant, so pass `subscriptionIds` whenever
   * the search can be narrowed — without it, common names such as
   * `NetworkWatcherRG` match in every readable subscription.
   */
  resolveResourceGroups(
    names: string[],
    subscriptionIds?: string[],
  ): Promise<ResolvedResourceGroup[]>;

  /** Tag values already in use for `tagKey`, most frequent first. */
  getTagValueSuggestions(
    subscriptionIds: string[],
    tagKey: string,
  ): Promise<TagValueSuggestion[]>;

  /**
   * Effective tag-write access for each ARM scope, keyed by scope. Scopes inherit
   * downwards, so an `allowed` subscription answers for every resource group in
   * it.
   */
  getTagWriteAccess(scopes: string[]): Promise<Record<string, TagWriteAccess>>;

  /** Merges `tags` into the resource group, leaving its other tags untouched. */
  mergeTags(
    resourceGroupId: string,
    tags: Record<string, string>,
  ): Promise<void>;
}

export const armTagsApiRef = createApiRef<ArmTagsApi>({
  id: 'plugin.scorecard.arm-tags',
});

export class ArmRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ArmRequestError';
  }
}

const ARM_BASE_URL = 'https://management.azure.com';
const USER_IMPERSONATION_SCOPE =
  'https://management.azure.com/user_impersonation';
const RESOURCE_GRAPH_API_VERSION = '2022-10-01';
const TAGS_API_VERSION = '2021-04-01';
const PERMISSIONS_API_VERSION = '2022-04-01';
const TAG_WRITE_ACTION = 'Microsoft.Resources/tags/write';
const PERMISSION_CHECKS_IN_PARALLEL = 8;
const RESOURCE_GROUP_TYPE = 'microsoft.resources/subscriptions/resourcegroups';
const NAMES_PER_QUERY = 100;
const GRAPH_PAGE_SIZE = 1000;
const GRAPH_MAX_PAGES = 25;
const SUGGESTION_LIMIT = 50;

const TENANT_ID_CONFIG_KEY = 'app.azureResources.tenantId';
const CLIENT_ID_CONFIG_KEY = 'app.azureResources.clientId';

/**
 * Resource group names and subscription ids reach us from Watchtower and are
 * interpolated into KQL, so they are allowlisted rather than escaped. Azure
 * resource group names allow unicode letters, digits and `_ - . ( )`.
 */
const SAFE_RESOURCE_GROUP_NAME = /^[\p{L}\p{N}_\-.()]+$/u;
const SAFE_TAG_KEY = /^[\p{L}\p{N}_\-.]+$/u;
const SUBSCRIPTION_ID = /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;

export const isSafeResourceGroupName = (name: string): boolean =>
  SAFE_RESOURCE_GROUP_NAME.test(name);

/**
 * Azure stores tag keys with the casing they were written with and KQL is
 * case-sensitive when indexing a dynamic bag, so lookups try every casing the
 * estate is known to use.
 */
export const tagKeyCasings = (tagKey: string): string[] => {
  const lower = tagKey.toLowerCase();
  return Array.from(
    new Set([
      lower,
      lower.charAt(0).toUpperCase() + lower.slice(1),
      lower.toUpperCase(),
    ]),
  );
};

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const quoteList = (values: string[]): string =>
  values.map(value => `'${value}'`).join(',');

type GraphResourceGroupRow = {
  id?: string;
  name?: string;
  subscriptionId?: string;
  location?: string;
  tags?: Record<string, string> | null;
};

type GraphSuggestionRow = {
  tagValue?: string;
  occurrences?: number;
};

type ArmPermission = {
  actions?: string[];
  notActions?: string[];
};

/** RBAC action patterns use `*` as a wildcard spanning any characters. */
const matchesAction = (pattern: string, action: string): boolean => {
  const expression = pattern
    .split('*')
    .map(part => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('.*');
  return new RegExp(`^${expression}$`, 'i').test(action);
};

export const grantsTagWrite = (permissions: ArmPermission[]): boolean =>
  permissions.some(
    permission =>
      (permission.actions ?? []).some(action =>
        matchesAction(action, TAG_WRITE_ACTION),
      ) &&
      !(permission.notActions ?? []).some(action =>
        matchesAction(action, TAG_WRITE_ACTION),
      ),
  );

const toArmError = async (
  response: Response,
  fallbackMessage: string,
): Promise<ArmRequestError> => {
  let message = `${fallbackMessage} (${response.status} ${response.statusText})`;

  try {
    const body = await response.text();
    const parsed = body ? JSON.parse(body) : undefined;
    const azureMessage = parsed?.error?.message ?? parsed?.message;
    if (azureMessage) message = azureMessage;
  } catch {
    // keep the status-based message
  }

  const retryAfter = Number(response.headers?.get?.('Retry-After'));

  return new ArmRequestError(
    message,
    response.status,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  );
};

/**
 * The subset of `InteractiveBrowserCredential` this client needs, so tests can
 * hand in a stub without an MSAL popup.
 */
export type ArmCredential = {
  getToken(scopes: string | string[]): Promise<{ token: string } | null>;
  authenticate?(scopes: string | string[]): Promise<unknown>;
};

type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;

export type ArmTagsClientOptions = {
  configApi?: ConfigApi;
  /** Defaults to an `InteractiveBrowserCredential` built from app config. */
  credential?: ArmCredential;
  fetchImpl?: FetchLike;
};

const credentialFromConfig = (configApi?: ConfigApi): ArmCredential => {
  const tenantId = configApi?.getOptionalString(TENANT_ID_CONFIG_KEY);
  const clientId = configApi?.getOptionalString(CLIENT_ID_CONFIG_KEY);

  if (!tenantId || !clientId) {
    throw new Error(
      'tenantId and clientId must be provided in the app config!',
    );
  }

  return new InteractiveBrowserCredential({
    tenantId,
    clientId,
    loginStyle: 'popup',
  });
};

/**
 * Reads and writes Azure resource group tags with the signed-in user's own
 * delegated token, so every call is bounded by their Azure RBAC rather than by a
 * service principal holding tenant-wide write access.
 */
export class ArmTagsClient implements ArmTagsApi {
  private readonly credential: ArmCredential;
  private readonly fetchImpl: FetchLike;

  constructor(options: ArmTagsClientOptions = {}) {
    this.credential =
      options.credential ?? credentialFromConfig(options.configApi);
    this.fetchImpl =
      options.fetchImpl ?? ((url, init) => fetch(url, init as RequestInit));
  }

  async resolveResourceGroups(
    names: string[],
    subscriptionIds?: string[],
  ): Promise<ResolvedResourceGroup[]> {
    const safeNames = Array.from(
      new Set(names.filter(name => isSafeResourceGroupName(name))),
    );
    if (!safeNames.length) return [];

    const scope = (subscriptionIds ?? []).filter(id =>
      SUBSCRIPTION_ID.test(id),
    );
    const resolved: ResolvedResourceGroup[] = [];

    for (const batch of chunk(safeNames, NAMES_PER_QUERY)) {
      const rows = await this.runGraphQuery<GraphResourceGroupRow>(
        `resourcecontainers
| where type =~ '${RESOURCE_GROUP_TYPE}'
| where name in~ (${quoteList(batch)})
| project id, name, subscriptionId, location, tags`,
        scope,
      );

      for (const row of rows) {
        if (!row.id || !row.name || !row.subscriptionId) continue;
        resolved.push({
          id: row.id,
          name: row.name,
          subscriptionId: row.subscriptionId,
          location: row.location ?? '',
          tags: row.tags ?? {},
        });
      }
    }

    return resolved;
  }

  async getTagValueSuggestions(
    subscriptionIds: string[],
    tagKey: string,
  ): Promise<TagValueSuggestion[]> {
    const safeIds = Array.from(
      new Set(subscriptionIds.filter(id => SUBSCRIPTION_ID.test(id))),
    );
    if (!safeIds.length || !SAFE_TAG_KEY.test(tagKey)) return [];

    const coalesced = tagKeyCasings(tagKey)
      .map(casing => `tostring(tags['${casing}'])`)
      .join(', ');

    const rows = await this.runGraphQuery<GraphSuggestionRow>(
      `resourcecontainers
| where type =~ '${RESOURCE_GROUP_TYPE}'
| where subscriptionId in~ (${quoteList(safeIds)})
| extend tagValue = coalesce(${coalesced})
| where isnotempty(tagValue) and tolower(tagValue) != 'no tag present'
| summarize occurrences = count() by tagValue
| order by occurrences desc
| limit ${SUGGESTION_LIMIT}`,
    );

    return rows
      .filter((row): row is Required<GraphSuggestionRow> => !!row.tagValue)
      .map(row => ({ value: row.tagValue, occurrences: row.occurrences ?? 0 }));
  }

  async getTagWriteAccess(
    scopes: string[],
  ): Promise<Record<string, TagWriteAccess>> {
    const uniqueScopes = Array.from(new Set(scopes.filter(Boolean)));
    const access: Record<string, TagWriteAccess> = {};

    for (const batch of chunk(uniqueScopes, PERMISSION_CHECKS_IN_PARALLEL)) {
      const results = await Promise.all(
        batch.map(scope => this.readTagWriteAccess(scope)),
      );
      batch.forEach((scope, index) => {
        access[scope] = results[index];
      });
    }

    return access;
  }

  private async readTagWriteAccess(scope: string): Promise<TagWriteAccess> {
    try {
      const token = await this.getAccessToken();
      const response = await this.fetchImpl(
        `${ARM_BASE_URL}${scope}/providers/Microsoft.Authorization/permissions?api-version=${PERMISSIONS_API_VERSION}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
          },
        },
      );

      if (!response.ok) return 'unknown';

      const body = await response.json();
      const permissions: ArmPermission[] = body?.value ?? [];
      return grantsTagWrite(permissions) ? 'allowed' : 'denied';
    } catch {
      return 'unknown';
    }
  }

  async mergeTags(
    resourceGroupId: string,
    tags: Record<string, string>,
  ): Promise<void> {
    const token = await this.getAccessToken();
    const url = `${ARM_BASE_URL}${resourceGroupId}/providers/Microsoft.Resources/tags/default?api-version=${TAGS_API_VERSION}`;

    const response = await this.fetchImpl(url, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ operation: 'Merge', properties: { tags } }),
    });

    if (!response.ok) {
      throw await toArmError(response, 'Failed to update tags');
    }
  }

  private async runGraphQuery<T>(
    query: string,
    subscriptions: string[] = [],
  ): Promise<T[]> {
    const token = await this.getAccessToken();
    const url = `${ARM_BASE_URL}/providers/Microsoft.ResourceGraph/resources?api-version=${RESOURCE_GRAPH_API_VERSION}`;
    const rows: T[] = [];
    let skipToken: string | undefined;
    let page = 0;

    do {
      const response = await this.fetchImpl(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          ...(subscriptions.length ? { subscriptions } : {}),
          options: {
            $top: GRAPH_PAGE_SIZE,
            ...(skipToken ? { $skipToken: skipToken } : {}),
          },
        }),
      });

      if (!response.ok) {
        throw await toArmError(response, 'Azure Resource Graph query failed');
      }

      const body = await response.json();
      rows.push(...((body?.data ?? []) as T[]));
      skipToken = body?.$skipToken;
      page += 1;
    } while (skipToken && page < GRAPH_MAX_PAGES);

    return rows;
  }

  /**
   * MSAL only prompts when it has to; a signed-in user is served from its cache.
   * `AuthenticationRequiredError` means the silent path is exhausted, which is
   * the one case worth an explicit popup (same handling as `useGetAzureToken`).
   */
  private async getAccessToken(): Promise<string> {
    let accessToken: { token: string } | null;

    try {
      accessToken = await this.credential.getToken(USER_IMPERSONATION_SCOPE);
    } catch (error: any) {
      if (error?.name !== 'AuthenticationRequiredError') throw error;
      await this.credential.authenticate?.(USER_IMPERSONATION_SCOPE);
      accessToken = await this.credential.getToken(USER_IMPERSONATION_SCOPE);
    }

    if (!accessToken?.token) {
      throw new Error('Failed to acquire Azure access token');
    }

    return accessToken.token;
  }
}
